import {
  DragEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog } from "@mui/material";
import CryptoJS from "crypto-js";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { ColDef, GridApi, GridReadyEvent, SideBarDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  useGetEmailTemplateContent,
  useGetEmailTemplateNames,
  useSaveEmailTemplate,
} from "../../queries/siteSettings";
import {
  VStack,
  HStack,
  ButtonPrimary,
  ButtonSecondary,
  isEsopViewer,
  ButtonPrimary1,
  Box,
  SettingHeader,
} from "../../components/utils";
import { Select } from "../../components/shared/Select";
import EmailTemplateEditorEnhanced from "./EmailTemplateEditorEnhanced";
import EmailTemplatePreviewDialog from "./EmailTemplatePreviewDialog";
import { SiteSettingsContext } from "./SiteSettings";
import {
  EmailTemplateSaveObject,
  ListOfEmailTemplates,
} from "../../types/SiteSettings";
import { Input } from "../../components/shared/InputField";
import { usePermissionStore } from "../../store/permissionStore";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Resource,
  Action as DefaultAction,
} from "../../utils/interfaces/Companies";
import SearchInput from "../../components/shared/SearchInput";
import { useUserDetailsV2 } from "../../queries";
import {
  deleteEmailTemplate,
  getEmailTemplateContentV1,
  getEmailTemplatePreview,
  sendTestEmail,
} from "../../api/siteSettings";

interface EditTemplateDialog {
  templateName?: string;
  templateContent?: string;
  editMode?: boolean;
  viewMode?: boolean;
  templateId?: string;
  templateType?: string;
  isEdited?: boolean;
  isCompanySpecific?: boolean;
  subject?: string;
  companyId?: string;
}
function EmailTemplateSettings() {
  const { permission } = usePermissionStore();
  const { data: userDetails } = useUserDetailsV2();
  const canPreview = userDetails?.emailId?.endsWith("@rulezero.com") ?? false;
  const {
    data: _emailTemplateNames,
    isFetched: fetchedEmailTemplateList,
    refetch: refetchTemplateNames,
  } = useGetEmailTemplateNames();
  const gridApi = useRef<GridApi | any>(null);
  const [editedTemplates, setEditedTemplates] = useState<
    Record<string, EditTemplateDialog>
  >({});
  const [selectedTemplate, setSelectedTemplate] = useState<EditTemplateDialog>(
    {}
  );
  const [viewclicked, setViewClicked] = useState(false);
  const [emailTemplateList, setEmailTemplateList] = useState<
    ListOfEmailTemplates[]
  >([]);
  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean;
    content: string;
    templateName: string;
    templateType: string;
    loading: boolean;
    sendingTest: boolean;
  }>({
    open: false,
    content: "",
    templateName: "",
    templateType: "",
    loading: false,
    sendingTest: false,
  });

  const { mutate: saveTemplate } = useSaveEmailTemplate();
  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      wrapText: true,
      autoHeaderHeight: true,
      minWidth: 100,
      resizable: true,
      filter: false,
      cellClass: "multiline text-xs font-medium text-gray-dark leading-5",
      filterParams: {},
      columnsMenuParams: {},
      menuTabs: [],
      wrapHeaderText: true,
    }),
    []
  );
  const handleAction = (
    rowData: ListOfEmailTemplates,
    action: "edit" | "view" = "view"
  ) => {
    const template = rowData?.content;
    if (!template) {
      getEmailTemplateContentV1(rowData.id).then((data) => {
        setSelectedTemplate({
          companyId: rowData.companyId,
          editMode: action === "edit",
          viewMode: action === "view",
          isCompanySpecific: Boolean(rowData?.companyId),
          subject: rowData.subject,
          templateContent: data.content,
          templateId: rowData?.id,
          templateType: rowData.templateType,
          templateName: rowData.name,
        });
      });
    } else {
      setSelectedTemplate({
        companyId: rowData.companyId,
        editMode: action === "edit",
        viewMode: action === "view",
        isCompanySpecific: Boolean(rowData?.companyId),
        subject: rowData.subject,
        templateContent: template,
        templateId: rowData?.id,
        templateType: rowData.templateType,
        templateName: rowData.name,
      });
    }
  };
  const handleView = (rowData: ListOfEmailTemplates) => {
    handleAction(rowData, "view");
  };
  const handleEdit = (rowData: ListOfEmailTemplates) => {
    handleAction(rowData, "edit");
  };
  function saveSingleTemplate(templateData: ListOfEmailTemplates) {
    if (!templateData) return;
    const templateToSave: EditTemplateDialog = {
      templateContent: templateData.content,
      templateId: templateData.id,
      subject: templateData.subject,
      isEdited: true,
    };
    saveEmailTemplate(templateToSave);
  }
  function discardSingleTemplate(templateId: string) {
    // 1. Revert changes in the row list
    setEmailTemplateList((prev) =>
      prev.map((item) =>
        item.id === templateId
          ? { ...item, isEdited: false, content: undefined }
          : item
      )
    );

    // 2. Remove the edited state
    setEditedTemplates((prev) => {
      const updated = { ...prev };
      delete updated[templateId];
      return updated;
    });
  }
  const handlePreview = (rowData: ListOfEmailTemplates) => {
    if (!canPreview) {
      toast("You don't have permission to preview email templates", {
        type: "error",
      });
      return;
    }
    setPreviewDialog({
      open: true,
      content: "",
      templateName: rowData.name,
      templateType: rowData.templateType,
      loading: true,
      sendingTest: false,
    });
    getEmailTemplatePreview(rowData.templateType)
      .then((data) => {
        setPreviewDialog((prev) => ({
          ...prev,
          content: data.html,
          loading: false,
        }));
      })
      .catch((err) => {
        toast(err.response?.data?.errorRaw || "Failed to load preview", {
          type: "error",
          autoClose: 10000,
        });
        setPreviewDialog({
          open: false,
          content: "",
          templateName: "",
          templateType: "",
          loading: false,
          sendingTest: false,
        });
      });
  };

  const handleSendTestFromPreview = () => {
    setPreviewDialog((prev) => ({ ...prev, sendingTest: true }));
    sendTestEmail(previewDialog.templateType)
      .then(() => {
        toast("Test email sent successfully", {
          type: "success",
          autoClose: 3000,
        });
        setPreviewDialog((prev) => ({ ...prev, sendingTest: false }));
      })
      .catch(() => {
        toast("Failed to send test email", { type: "error" });
        setPreviewDialog((prev) => ({ ...prev, sendingTest: false }));
      });
  };

  const handleDelete = (rowData: ListOfEmailTemplates) => {
    if (!rowData?.id || !rowData.overriddenByCompany || !rowData.companyId)
      return;
    deleteEmailTemplate(rowData?.id)
      .then((data) => {
        toast("Email Template deleted successfully", { type: "success" });
        refetchTemplateNames();
      })
      .catch((e) => {
        toast("Error while deleting template", { type: "error" });
      });
  };
  const columnDefs: ColDef[] = useMemo(() => {
    const colDef: ColDef[] = [
      {
        headerName: " NAME",
        field: "name",
        valueFormatter: (params) =>
          params.value.charAt(0).toUpperCase() + params.value.slice(1),
        width: 300,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: "20px",
          paddingTop: "15px",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        },
      },
      {
        headerName: "DESCRIPTION",
        field: "description",
        width: 300,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: "20px",
          paddingTop: "15px",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        },
      },
      {
        headerName: "RECIPIENT",
        field: "recipient",
        valueFormatter: (params) =>
          params.value.charAt(0).toUpperCase() + params.value.slice(1),
      },
      {
        headerName: "COMPANY SPECIFIC",
        field: "overriddenByCompany",
        width: 120,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      {
        headerName: "SUBJECT",
        field: "subject",
        cellClass: "truncate max-w-xs",
        width: 300,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: "20px",
          paddingTop: "15px",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        },
      },
      {
        headerName: "ACTIONS",
        pinned: "right",
        filter: false,
        sortable: false,
        width: 190,
        cellRenderer: (params: { data: ListOfEmailTemplates }) => {
          const data = params.data;
          const isEdited = data.isEdited;
          return (
            <Box className="mt-2 px-auto py-auto">
              <div className="flex items-center justify-center gap-1.5">
                <button
                  onClick={() => handleView(data)}
                  title="View"
                  className="transition-transform text-orange-501 hover:scale-110"
                >
                  <Icon icon="ph:eye" width="18" height="18" />
                </button>
                <button
                  onClick={() => handleEdit(data)}
                  title="Edit"
                  className="transition-transform text-orange-501 hover:scale-110"
                >
                  <Icon icon="akar-icons:pencil" width="18" height="18" />
                </button>
                {canPreview && (
                  <button
                    onClick={() => handlePreview(data)}
                    title="Preview Mock Email"
                    className="transition-transform text-orange-501 hover:scale-110"
                  >
                    <Icon
                      icon="mdi:email-open-outline"
                      width="18"
                      height="18"
                    />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(data)}
                  title="Delete"
                  className={`hover:scale-110 transition-transform ${
                    !data.overriddenByCompany
                      ? "cursor-not-allowed text-gray-400"
                      : "text-orange-501"
                  }`}
                >
                  <Icon icon="material-symbols:delete" width="18" height="18" />
                </button>

                {isEdited && (
                  <>
                    <span className="mx-1 text-gray-300">|</span>
                    <button
                      onClick={() => saveSingleTemplate(data)}
                      title="Save Changes"
                      className="text-green-600 transition-transform hover:scale-110"
                    >
                      <Icon icon="mdi:content-save" width="18" height="18" />
                    </button>
                    <button
                      onClick={() => discardSingleTemplate(data.id)}
                      title="Discard Changes"
                      className="text-red-500 transition-transform hover:scale-110"
                    >
                      <Icon
                        icon="material-symbols:cancel-outline"
                        width="18"
                        height="18"
                      />
                    </button>
                  </>
                )}
              </div>
            </Box>
          );
        },
      },
    ];
    return colDef;
  }, []);
  const hasUnsavedChanges = emailTemplateList.some(
    (template) => template.isEdited
  );

  function saveEmailTemplate(template?: EditTemplateDialog) {
    if (!template) template = selectedTemplate;
    if (!template.isEdited) {
      toast("No Content to Save", { type: "error", autoClose: 3000 });
    } else if (!template.templateContent || template.templateContent === "") {
      toast("No Content to Save", { type: "error", autoClose: 3000 });
    } else {
      const templateObj: EmailTemplateSaveObject = {
        templateId: template?.templateId || "",
        isEdited: true,
        subject: template.subject,
        emailTemplateBodyBase64: btoa(
          unescape(encodeURIComponent(template.templateContent))
        ),
      };
      saveTemplate(templateObj, {
        onSuccess: (_) => {
          toast("Template Saved Successfully", {
            type: "success",
            autoClose: 3000,
          });
          if (!template || !template.templateId) return;
          setEmailTemplateList((prev) =>
            prev.map((item) =>
              item.id === template?.templateId
                ? { ...item, isEdited: false }
                : item
            )
          );
          if (!template || !template.templateId) return;
          setEditedTemplates((prev) => {
            const updated = { ...prev };
            if (template)
              if (updated) delete updated[template.templateId || ""];
            return updated;
          });
          refetchTemplateNames();
        },
      });
    }
  }

  useEffect(() => {
    if (!_emailTemplateNames) return;
    const mergedList = _emailTemplateNames.map((template) => {
      const edited = editedTemplates[template.id];
      return {
        ...template,
        ...(edited
          ? {
              isEdited: edited.isEdited,
              subject: edited.subject ?? template.subject,
              content: edited.templateContent,
            }
          : {}),
      };
    });

    setEmailTemplateList(mergedList);
  }, [_emailTemplateNames, editedTemplates]);

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.current = params.api;
  };

  return (
    <Box className="w-full p-4 bg-white rounded-lg ">
      <VStack className="justify-between p-4 gap-9">
        <Box className="items-center mt-6 text-lg font-medium text-gray-dark">
          <SettingHeader
            title="Email Template Settings"
            description="Configure your email template settings"
          />
        </Box>
        <HStack className="justify-between w-full border-t-2 border-dotted pt-6">
          <VStack className="items-center w-1/3 gap-4">
            <SearchInput
              className=""
              placeholder="Search"
              onChange={(e: any) => {
                gridApi.current.setQuickFilter(e.target.value);
              }}
            />
          </VStack>
          <VStack>
            {hasUnsavedChanges && (
              <Box className="flex items-center justify-end gap-4 p-2 text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md shadow-sm">
                <div className="text-sm font-medium">
                  You have unsaved changes. Don’t forget to save or discard
                  them.
                </div>
                <div className="flex gap-2">
                  <button
                    className="text-sm text-blue-600 hover:underline"
                    onClick={() => setEditedTemplates({})}
                  >
                    Discard All
                  </button>
                </div>
              </Box>
            )}
          </VStack>
        </HStack>

        <HStack className="justify-between w-full">
          <Box
            style={{
              height: `${
                (emailTemplateList.length >= 10
                  ? 10
                  : emailTemplateList.length + 3) * 60
              }px`,
            }}
            className="w-full h-full overflow-x-auto bg-black ag-theme-material"
          >
            <AgGridReact
              alwaysShowHorizontalScroll
              rowClass={
                "border-t border-dashed cursor-pointer hover:bg-slate-50 "
              }
              onGridReady={onGridReady}
              defaultColDef={defaultColDef}
              rowData={emailTemplateList}
              suppressCopyRowsToClipboard={true}
              suppressCopySingleCellRanges={true}
              suppressCellFocus={true}
              suppressMenuHide={true}
              sideBar={sideBar}
              columnDefs={columnDefs}
              rowMultiSelectWithClick={true}
              suppressRowClickSelection={true}
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
              }
            ></AgGridReact>
          </Box>
        </HStack>
        <HStack className="justify-between">
          <Dialog
            open={
              (selectedTemplate.templateContent && selectedTemplate.viewMode) ||
              false
            }
            onClose={() => setSelectedTemplate({})}
            maxWidth="md"
          >
            <div className="w-[900px] h-[900px]">
              <div className="p-4 px-10 mt-4 ml-3 text-lg font-medium border-b stone-700 ">
                <h6 className="flex justify-between">
                  View: {selectedTemplate?.templateName?.toUpperCase()}
                  <span
                    onClick={() => setSelectedTemplate({})}
                    className="cursor-pointer"
                  >
                    X
                  </span>
                </h6>
              </div>
              {selectedTemplate.templateContent && (
                <div
                  key={selectedTemplate.templateId}
                  className="overflow-y-scroll"
                  dangerouslySetInnerHTML={{
                    __html:
                      selectedTemplate?.templateContent ||
                      `<div> No Content Exists </div>`,
                  }}
                ></div>
              )}
            </div>
          </Dialog>
        </HStack>
        <EmailTemplatePreviewDialog
          open={previewDialog.open}
          templateName={previewDialog.templateName}
          content={previewDialog.content}
          loading={previewDialog.loading}
          sendingTest={previewDialog.sendingTest}
          onSendTest={handleSendTestFromPreview}
          onClose={() =>
            setPreviewDialog({
              open: false,
              content: "",
              templateName: "",
              templateType: "",
              loading: false,
              sendingTest: false,
            })
          }
        />

        <HStack className="justify-between">
          <Dialog open={selectedTemplate.editMode || false} maxWidth={false}>
            <div className="w-[1100px]">
              <EmailTemplateEditorEnhanced
                templateName={selectedTemplate?.templateName || ""}
                templateContent={selectedTemplate?.templateContent || ""}
                subject={selectedTemplate?.subject}
                templateType={selectedTemplate?.templateType || ""}
                onClose={(data: { editedData?: string; subject?: string }) => {
                  const templateId = selectedTemplate.templateId;
                  if (!templateId) return;
                  if (data.editedData) {
                    setEditedTemplates((prev) => ({
                      ...prev,
                      [templateId]: {
                        ...prev[templateId],
                        templateId,
                        templateName: selectedTemplate.templateName || "",
                        templateContent: data.editedData,
                        subject: data.subject,
                        isEdited: true,
                        editMode: false,
                        isCompanySpecific:
                          selectedTemplate.isCompanySpecific ?? false,
                        isNewOverride: !selectedTemplate.isCompanySpecific,
                      },
                    }));
                  }
                  setSelectedTemplate({});
                }}
              />
            </div>
          </Dialog>
        </HStack>
      </VStack>
      <HStack className="justify-between mt-12">
        <ButtonPrimary1
          type="reset"
          onClick={() => setEditedTemplates({})}
          className="mr-8 text-red-500"
        >
          Cancel
        </ButtonPrimary1>
      </HStack>
    </Box>
  );
}

const EmailEditor = () => {
  const [column1Content, setColumn1Content] = useState(
    "Type in English here..."
  );
  const [column2Items] = useState(["Item 1", "Item 2", "Item 3"]); // Add more items as needed
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  const allowDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const drag = (event: DragEvent<HTMLDivElement>, item: string) => {
    event.dataTransfer.setData("text/plain", item);
  };

  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const data = event.dataTransfer.getData("text/plain");
    const cursorPosition = getCursorPosition(event.target);

    const textBefore = column1Content.substring(0, cursorPosition);
    const textAfter = column1Content.substring(cursorPosition);

    setColumn1Content(textBefore + data + textAfter);
  };

  const getCursorPosition = (element: any) => {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        return preCaretRange.toString().length;
      }
    }
    return 0;
  };

  const toggleMode = () => {
    setIsHtmlMode(!isHtmlMode);
  };

  return <div></div>;
};

export default EmailTemplateSettings;
